import { Button, Stack, Table, Text, TextInput, Title } from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import {
  EmptyState,
  ErrorState,
  InlineError,
  LoadingSkeleton,
  MoneyField,
  SectionCard,
  StateBadge,
} from "../../components/ui";
import { Page } from "../../shell/Page";
import { api, type PriceHistoryEntry, type Product } from "../../lib/api";
import { formatINR } from "../../lib/money";

/**
 * The Store Manager's price list (Q7).
 *
 * "These prices vary time to time, so a historical price with an effective
 * date is important. The Store manager should be the only one who can update
 * this. Maintain a history of the price changes."
 *
 * So: append-only. There is no edit and no delete anywhere on this screen,
 * because a price that a bill was already raised against has to stay
 * reproducible - the bill is a tax document. A correction is a new row with
 * its own effective date, and the price that applies to any given day is the
 * latest row not in the future.
 */
export function PriceList() {
  const [products, setProducts] = useState<Product[]>();
  const [query, setQuery] = useState("");
  const [error, setError] = useState<string>();

  const [openId, setOpenId] = useState<string>();
  const [history, setHistory] = useState<PriceHistoryEntry[]>();
  const [historyError, setHistoryError] = useState<string>();

  const [dealerPrice, setDealerPrice] = useState("");
  const [mrp, setMrp] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [sourceNote, setSourceNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<string>();

  async function loadProducts() {
    try {
      const { products: loaded } = await api.products();
      setProducts(loaded);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not load products.");
    }
  }

  useEffect(() => {
    void loadProducts();
  }, []);

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return products ?? [];
    return (products ?? []).filter(
      (product) =>
        product.sku.toLowerCase().includes(needle) ||
        product.name.toLowerCase().includes(needle),
    );
  }, [products, query]);

  const unpriced = (products ?? []).filter((product) => product.dealerPrice === null);

  async function open(product: Product) {
    if (openId === product.id) {
      setOpenId(undefined);
      return;
    }
    setOpenId(product.id);
    setHistory(undefined);
    setHistoryError(undefined);
    setSaved(undefined);
    // Pre-fill with what is in force, so a change is a small edit rather than
    // re-typing a figure that is easy to get wrong.
    setDealerPrice(product.dealerPrice ?? "");
    setMrp(product.mrp ?? "");
    setEffectiveFrom(new Date().toISOString().slice(0, 10));
    setSourceNote("");
    try {
      const { prices } = await api.priceHistory(product.id);
      setHistory(prices);
    } catch (caught) {
      setHistoryError(
        caught instanceof Error ? caught.message : "Could not load the price history.",
      );
    }
  }

  async function record(product: Product) {
    const dealer = Number(dealerPrice);
    if (!Number.isFinite(dealer) || dealer < 0) {
      setHistoryError("Enter the dealer price.");
      return;
    }
    setSaving(true);
    setHistoryError(undefined);
    try {
      await api.recordPrice(product.id, {
        dealerPrice: dealer,
        mrp: mrp.trim() === "" ? null : Number(mrp),
        effectiveFrom,
        ...(sourceNote.trim() ? { sourceNote: sourceNote.trim() } : {}),
      });
      const { prices } = await api.priceHistory(product.id);
      setHistory(prices);
      setSaved(
        new Date(effectiveFrom) > new Date()
          ? `Saved. It takes effect on ${new Date(effectiveFrom).toLocaleDateString()}.`
          : "Saved and in force.",
      );
      // The list's "price today" column is now stale for this product.
      await loadProducts();
    } catch (caught) {
      setHistoryError(
        caught instanceof Error ? caught.message : "Could not save the price.",
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <Page
      title="Price list"
      subtitle="Dealer price and MRP, with full history"
      width="wide"
    >
      {error && !products && <ErrorState message={error} />}
      {error && products && <InlineError>{error}</InlineError>}
      {!products && !error && <LoadingSkeleton variant="card" count={4} />}

      {products && (
        <SectionCard title="Find a product">
          <Stack gap="sm">
            <TextInput
              id="price-search"
              label="Search"
              type="search"
              value={query}
              placeholder="SKU or name"
              leftSection={<IconSearch size={16} stroke={1.8} />}
              onChange={(event) => setQuery(event.currentTarget.value)}
            />
            <Text fz="sm" c="gray.6">
              {shown.length} of {products.length} products
              {unpriced.length > 0 && ` · ${unpriced.length} still unpriced`}
            </Text>
            {unpriced.length > 0 && (
              <Text fz="sm" c="gray.6">
                An unpriced product cannot be put on a day order at all — there
                would be no price to record against the line.
              </Text>
            )}
          </Stack>
        </SectionCard>
      )}

      {products && shown.length === 0 && (
        <EmptyState title="No products match that" />
      )}

      {shown.map((product) => (
        <SectionCard
          key={product.id}
          title={product.sku}
          action={
            <Button variant="default" size="sm" onClick={() => void open(product)}>
              {openId === product.id ? "Close" : "History & change"}
            </Button>
          }
        >
          <Stack gap="sm">
            <Stack gap={4}>
              <Text fz="lg" fw={600} c="gray.9">
                {product.name}
              </Text>
              {product.dealerPrice === null ? (
                <div>
                  <StateBadge state="dlvPending" label="No price set" />
                </div>
              ) : (
                <Text fz="sm" c="gray.6" className="num">
                  {formatINR(product.dealerPrice)} dealer
                  {product.mrp ? ` · ${formatINR(product.mrp)} MRP` : ""}
                  {product.priceEffectiveFrom
                    ? ` · since ${new Date(product.priceEffectiveFrom).toLocaleDateString()}`
                    : ""}
                </Text>
              )}
            </Stack>

            {openId === product.id && (
              <Stack gap="md">
                {historyError && <InlineError>{historyError}</InlineError>}
                {saved && (
                  <Text fz="md" c="teal.8">
                    {saved}
                  </Text>
                )}

                <Stack gap="sm">
                  <Title order={6} tt="uppercase" c="gray.6">
                    New price
                  </Title>
                  <MoneyField
                    id={`dealer-${product.id}`}
                    label="Dealer price"
                    withAsterisk
                    min={0}
                    value={dealerPrice}
                    onChange={(value) => setDealerPrice(String(value))}
                  />
                  <MoneyField
                    id={`mrp-${product.id}`}
                    label="MRP"
                    description="Optional."
                    min={0}
                    value={mrp}
                    onChange={(value) => setMrp(String(value))}
                  />
                  <TextInput
                    id={`from-${product.id}`}
                    label="In force from"
                    type="date"
                    value={effectiveFrom}
                    onChange={(event) => setEffectiveFrom(event.currentTarget.value)}
                    description="A future date loads the next list early; a past date corrects one loaded late. Bills already raised keep the price they used."
                  />
                  <TextInput
                    id={`note-${product.id}`}
                    label="Where it came from"
                    description="Optional."
                    maxLength={300}
                    placeholder="e.g. Amaron DP & MRP list, Sept"
                    value={sourceNote}
                    onChange={(event) => setSourceNote(event.currentTarget.value)}
                  />
                  <Button
                    size="md"
                    fullWidth
                    loading={saving}
                    disabled={dealerPrice.trim() === ""}
                    onClick={() => void record(product)}
                  >
                    Add this price
                  </Button>
                </Stack>

                <Stack gap="sm">
                  <Title order={6} tt="uppercase" c="gray.6">
                    History
                  </Title>
                  {!history && !historyError && (
                    <LoadingSkeleton variant="table-row" count={3} />
                  )}
                  {history?.length === 0 && (
                    <Text fz="md" c="gray.6">
                      No price has ever been recorded.
                    </Text>
                  )}
                  {history && history.length > 0 && (
                    <Table.ScrollContainer minWidth={460}>
                      <Table verticalSpacing="xs" horizontalSpacing="sm">
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>From</Table.Th>
                            <Table.Th ta="right">Dealer</Table.Th>
                            <Table.Th ta="right">MRP</Table.Th>
                            <Table.Th>Recorded</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {history.map((entry) => (
                            <Table.Tr key={entry.id}>
                              <Table.Td className="num">
                                {new Date(entry.effectiveFrom).toLocaleDateString()}
                              </Table.Td>
                              <Table.Td ta="right" className="num">
                                {formatINR(entry.dealerPrice)}
                              </Table.Td>
                              <Table.Td ta="right" className="num">
                                {entry.mrp ? formatINR(entry.mrp) : "—"}
                              </Table.Td>
                              <Table.Td>
                                <Text fz="sm" c="gray.9">
                                  {entry.recordedBy.name}
                                </Text>
                                <Text fz="xs" c="gray.6">
                                  {new Date(entry.recordedAt).toLocaleDateString()}
                                  {entry.sourceNote ? ` · ${entry.sourceNote}` : ""}
                                </Text>
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    </Table.ScrollContainer>
                  )}
                </Stack>
              </Stack>
            )}
          </Stack>
        </SectionCard>
      ))}
    </Page>
  );
}
